/*
 * Search service abstraction for angular
 *
 * example
 *   var search = new Search({
 *     searchUrl: '/path/to/search',
 *     onQueryCompleteCallback: [function(response)]
 *   };
 *   search.defineFilter('keywords');
 *   search.addFilterValue(search.filter.keywords, 'testquery');
 *   search.search();
 *   // now the callback method will be invoked, passing the
 *   // search response for 'testquery' in
 */

//= require devex/urls
//= require devex/search/filter
//= require devex/search/pagination
//= require devex/search/sorting
//= require devex/browser_url

devex = devex || {};
devex.search = devex.search || {};

devex.search.SearchJS = function (options) {
  this.SUPPORTED_EVENTS = ['queryComplete', 'queryException', 'paramChange'];
  this.searchUrl = options.searchUrl;
  this.results = [];
  this.aggregations = {};
  this.filter = {};
  this.enableUrlUpdate = !options.disableUrlUpdate;
  this.browserUrl = devex.urls.BrowserUrl.getInstance();
  this.registeredParams = [];
  this.browserUrlSetupCompleted = false;
  this.queryInProgress = false;
  this.latency = null;
  this.response = null;
  this.defaultPageSize = options.defaultPageSize || 10;
  this.pagination = new devex.search.Pagination(1, this.defaultPageSize);
  this.additionalParams = options.additionalParams || {};
  this._eventListeners = {};
  // Flag to perform a search or not when params change
  this.triggerSearchOnParamChange = true;
  this.defaultSortingField = options.defaultSortingField || '';
  this.sorting = new devex.search.Sorting(this.defaultSortingField);
};

// EVENTS

devex.search.SearchJS.prototype.on = function (event, callback) {
  if (this.SUPPORTED_EVENTS.indexOf(event) === -1) {
    throw 'Event "' + event + '" is not supported';
  }
  if (typeof callback !== 'function') {
    throw 'Callback "' + callback + '" for "' + event + '" needs to be function';
  }
  if (!this._eventListeners[event]) {
    this._eventListeners[event] = [];
  }
  this._eventListeners[event].push(callback);
};

devex.search.SearchJS.prototype.trigger = function (event, payload) {
  _.each(this._eventListeners[event], function (callback) {
    callback(payload);
  });
};

// NOTIFIERS

devex.search.SearchJS.prototype.notifyParamChange = function () {
  if (this.triggerSearchOnParamChange) {
    this.pagination.reset();
    this.search();
  }

  this.trigger('paramChange');
};

// GETTERS

devex.search.SearchJS.prototype.getFilterValues = function (options) {
  var params = {};
  options = options || {};
  _.each(this.filter, function (filter, key) {
    if (filter.isActive()) {
      // eslint-disable-next-line no-useless-assignment
      var value = null;
      if (options.context === 'backend') {
        value = filter.getValueForBackend();
      } else {
        value = filter.getValue();
      }
      if (filter.attachToRoot) {
        params[key] = value;
      } else {
        if (!params.filter) {
          params.filter = {};
        }
        params.filter[key] = value;
      }
    }
  });
  return params;
};

/** Collects params from several sources
 * (filter, pagination, additionalParams)
 *
 * @param {object} options Filter options
 *   includeFilter[bool]
 *   includePagination[bool]
 *   includeSorting[bool]
 *   includeAdditionalParams[bool]
 * @returns {object} The collection of params
 */
devex.search.SearchJS.prototype.getParams = function (options) {
  options = _.extend(
    {
      includeFilter: false,
      includePagination: false,
      includePageSize: false,
      includeSorting: false,
      includeAdditionalParams: false,
    },
    options
  );
  var params = {};
  var filter = this.getFilterValues({ context: options.context }) || null;
  var pagination = this.pagination.getParams(options.includePageSize);

  if (options.includeFilter && filter) {
    _.extend(params, filter);
  }

  if (options.includePagination && pagination) {
    _.extend(params, pagination);
  }

  var sortingParams = this.sorting.getParams();

  if (options.includeSorting && Object.keys(sortingParams.sorting).length) {
    _.extend(params, sortingParams);
  }

  if (options.includeAdditionalParams && this.additionalParams) {
    _.extend(params, this.additionalParams);
  }
  return params;
};

devex.search.SearchJS.prototype.getResultIDs = function () {
  return _.map(this.results, function (result) {
    return result.id;
  });
};

// INIT METHODS

devex.search.SearchJS.prototype.reset = function () {
  this.pagination.reset();
  this.sorting.reset();
  _.each(this.filter, function (filter, key) {
    filter.reset();
  });
};

devex.search.SearchJS.prototype.loadParams = function (params) {
  this.reset();

  /*
   map filters on root level to filters, if they have 'attachToRoot' set to
   true. Like this, urls like ?query[]=abc still are assigned to the
   filter.query filter object, eventhough they are not nested under
   filter
   */
  var rootFilters = _.filter(this.filter, function (filter) {
    return filter.attachToRoot;
  });
  params.filter = params.filter || {};
  _.each(rootFilters, function (filter) {
    if (params[filter.paramName]) {
      params.filter[filter.paramName] = params[filter.paramName];
    }
  });

  if (params.filter) {
    _.each(this.filter, function (filter, key) {
      if (params.filter[key]) {
        filter.setValue(params.filter[key]);
      }
    });
  }

  if (params.sorting) {
    this.sorting.setParams({
      sorting: {
        field: params.sorting.field,
        order: params.sorting.order,
      },
    });
  }

  this.pagination.setParams(params);
};

/* addFilter
 * Accepts Filter object or regular object. If regular object,
 * it gets converted into a Filter object automatically.
 */

devex.search.SearchJS.prototype.addFilter = function (filter) {
  if (!(filter instanceof devex.search.Filter)) {
    filter = new devex.search.Filter(filter);
  }
  this.filter[filter.name] = filter;

  if (this.enableUrlUpdate) {
    var browserUrlParam = {};
    var getValueFunction = function () {
      return filter.getValue();
    };

    if (filter.attachToRoot) {
      browserUrlParam[filter.name] = getValueFunction;
    } else {
      browserUrlParam.filter = {};
      browserUrlParam.filter[filter.name] = getValueFunction;
    }

    var defaultValue = filter.default;

    var setValueFunction = function (value) {
      return filter.setValue(value);
    };

    this.registeredParams.push(
      this.browserUrl.addParam(browserUrlParam, setValueFunction, defaultValue)
    );
  }
};

// URL HANDLING

devex.search.SearchJS.prototype.updateURL = function () {
  this.browserUrl.updateUrl();
};

devex.search.SearchJS.prototype.getURL = function () {
  var params = this.getParams({
    includeFilter: true,
    includePagination: true,
    includePageSize: true,
    includeSorting: true,
    includeAdditionalParams: true,
    context: 'backend',
  });
  return this.searchUrl + '?' + $.param(params);
};

/**
 * Setup the BrowserUrlJS library, executed on first `.search` invocation
 *
 * @returns {undefined}
 */
devex.search.SearchJS.prototype.setupBrowserUrl = function () {
  // setup pagination and sorting
  var that = this,
      pageGetter = function () {
        return that.pagination.page;
      },
      pageSetter = function (page) {
        that.pagination.page = parseInt(page);
      },
      pageSizeGetter = function () {
        return that.pagination.pageSize;
      },
      pageSizeSetter = function (size) {
        that.pagination.pageSize = parseInt(size);
      },
      sortingOrderGetter = function () {
        return that.sorting.order;
      },
      sortingFieldGetter = function () {
        return that.sorting.field;
      },
      sortingOptionSetter = function (field) {
        that.sorting[field] = field;
      };
  that.registeredParams.push(
    that.browserUrl.addParam({ page: { number: pageGetter } }, pageSetter, 1),
    that.browserUrl.addParam(
      { page: { size: pageSizeGetter } },
      pageSizeSetter,
      that.defaultPageSize
    ),
    that.browserUrl.addParam({ sorting: { order: sortingOrderGetter } }, sortingOptionSetter, ''),
    that.browserUrl.addParam(
      { sorting: { field: sortingFieldGetter } },
      sortingOptionSetter,
      that.defaultSortingField
    )
  );

  // setup final callback after URL has changed
  that.browserUrl.on('afterParametersChanged', that.registeredParams, function () {
    that.search();
  });

  that.browserUrlSetupCompleted = true;
};

// SEARCH

devex.search.SearchJS.prototype.search = function () {
  var that = this;
  var timer = Date.now();
  that.queryInProgress = true;

  if (that.enableUrlUpdate) {
    if (!that.browserUrlSetupCompleted) {
      that.setupBrowserUrl();
    }
    that.updateURL();
  }

  return $.get(that.getURL())
    .done(function (response) {
      that.response = response;
      that.results = response.data;
      that.aggregations = response.aggregations || {};
      that.suggestions = response.suggestions || [];
      that.pagination.setParams(response);
      that.queryInProgress = false;
      that.trigger('queryComplete', response);
    })
    .fail(function (response) {
      that.queryInProgress = false;
      that.trigger('queryException', response);
    })
    .always(function () {
      that.latency = Date.now() - timer;
    });
};
